import { Link } from 'react-router-dom';
import { useInView } from '../../hooks/useInView';

const areas = [
  { name: 'Yardley', primary: true },
  { name: 'Newtown', primary: true },
  { name: 'Langhorne', primary: true },
  { name: 'Bristol', primary: false },
  { name: 'New Hope', primary: false },
  { name: 'Doylestown', primary: false },
  { name: 'Washington Crossing', primary: false },
  { name: 'Morrisville', primary: false },
  { name: 'Fairless Hills', primary: false },
  { name: 'Levittown', primary: false },
  { name: 'Warminster', primary: false },
  { name: 'Warrington', primary: false },
  { name: 'Horsham', primary: false },
  { name: 'Southampton', primary: false },
  { name: 'Richboro', primary: false },
];

interface ServiceAreasSectionProps {
  localCity?: string;
}

export default function ServiceAreasSection({ localCity = 'Yardley' }: ServiceAreasSectionProps) {
  const { ref, inView } = useInView();

  return (
    <section
      ref={ref as React.RefObject<HTMLElement>}
      className="bg-[#0d0d0d] py-28 border-t border-[#d4af37]/8"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left */}
          <div className={`transition-all duration-700 ${inView ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-6'}`}>
            <div className="flex items-center gap-4 mb-5">
              <span className="w-8 h-px bg-[#d4af37]/30" />
              <p className="text-[#d4af37] uppercase tracking-[0.3em] text-[10px]" style={{ fontFamily: 'Inter, sans-serif' }}>
                Service Coverage
              </p>
            </div>
            <h2
              className="text-4xl md:text-5xl lg:text-6xl text-white font-light leading-tight mb-6"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              Serving Clients Across<br />
              <em className="text-[#d4af37] not-italic">Bucks County, PA</em>
            </h2>
            <p className="text-[#5a5a5a] text-sm leading-relaxed mb-10 max-w-md" style={{ fontFamily: 'Inter, sans-serif' }}>
              Clients searching for personalized holistic healing near {localCity} choose Khalsa Healing for its tailored, results-driven approach and elevated therapeutic experience. Sessions available at our Yardley studio by appointment.
            </p>
            <Link
              to="/locations"
              className="inline-flex items-center gap-2 border border-[#d4af37]/25 hover:border-[#d4af37] text-[#d4af37] px-7 py-3 rounded-full text-xs uppercase tracking-[0.15em] transition-all duration-300 cursor-pointer whitespace-nowrap"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              View Service Areas
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line text-sm" />
              </div>
            </Link>
          </div>

          {/* Right: Area grid */}
          <div className={`transition-all duration-700 ${inView ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-6'}`} style={{ transitionDelay: '150ms' }}>
            <div className="bg-[#0a0a0a] border border-[#1a1a1a] rounded-2xl p-8">
              <p className="text-[#3a3a3a] text-xs uppercase tracking-[0.2em] mb-6" style={{ fontFamily: 'Inter, sans-serif' }}>
                Communities Served
              </p>
              <div className="flex flex-wrap gap-2.5">
                {areas.map((area, idx) => (
                  <Link
                    key={idx}
                    to="/locations"
                    className={`px-4 py-2 rounded-full text-xs transition-all duration-300 cursor-pointer whitespace-nowrap ${
                      area.primary
                        ? 'bg-[#d4af37]/10 border border-[#d4af37]/30 text-[#d4af37] hover:bg-[#d4af37]/20'
                        : 'border border-[#1e1e1e] text-[#5a5a5a] hover:border-[#d4af37]/20 hover:text-[#8a8a8a]'
                    }`}
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    {area.name}
                  </Link>
                ))}
              </div>
              <div className="mt-8 pt-6 border-t border-[#1a1a1a] flex items-center gap-3">
                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#d4af37]/10 flex-shrink-0">
                  <i className="ri-map-pin-2-line text-[#d4af37] text-sm" />
                </div>
                <p className="text-[#5a5a5a] text-xs" style={{ fontFamily: 'Inter, sans-serif' }}>
                  Based at <strong className="text-[#8a8a8a]">90 West Afton Ave. #204, Yardley, PA 19067</strong>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
